import {
  getLoanStatusesByLoanId,
  getOrderStatusesByLoanId,
  LoanStatus,
  LoanStatusExternal,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';

import {
  LoanStatusExternalProgressStages,
  LoanStatusViewStatus,
  MilestoneProgressStages,
  NonActiveProgressStages,
  OrderStatusViewStatus,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/_shared/types';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { Milestone } from '@/components/modules/Funnel/types/Milestone';
import { orderStatusesMapper } from '@/components/shared/components/Progress/utils/orderStatusesMapper';

export class OrderProgress extends AbstractProgress {
  private readonly NON_ACTIVE_STATUSES = Object.keys(ORDER_PROGRESS_NON_ACTIVE_STAGES) as LoanStatusExternal[];
  private readonly COMPLETED_STATUSES = [LoanStatusExternal.CAPTURED, LoanStatusExternal.PAID];

  protected _statuses: OrderStatusViewStatus[] | undefined = undefined;

  constructor(loanId: string, paymentMethodType: PaymentMethodType) {
    super(loanId, paymentMethodType, ORDER_PROGRESS_STAGES);
  }

  protected async fetchStatuses(): Promise<OrderStatusViewStatus[]> {
    const [orderStatuses, loanStatuses] = await Promise.all([this.getOrderStatuses(), this.getLoanStatuses()]);
    return orderStatusesMapper(orderStatuses, loanStatuses);
  }

  private async getOrderStatuses(): Promise<OrderStatusViewStatus[] | undefined> {
    try {
      const orderStatuses = await getOrderStatusesByLoanId({ loanId: this._paymentMethodId });
      return orderStatuses.statuses;
    } catch (error) {
      console.error('Error fetching order statuses', error);
      return;
    }
  }

  private async getLoanStatuses(): Promise<LoanStatusViewStatus[] | undefined> {
    try {
      const loanStatuses = await getLoanStatusesByLoanId({ loanId: this._paymentMethodId });
      return loanStatuses.statuses;
    } catch (error) {
      console.error('Error fetching loan statuses', error);
      return;
    }
  }

  protected getStatusName(status: LoanStatus | LoanStatusExternal) {
    return OrderProgress.getStatusName(status);
  }

  public static getStatusName(status: LoanStatus | LoanStatusExternal) {
    switch (status) {
      case LoanStatus.STATUS_ANALYSING:
        return 'PERSONAL_INFORMATION';
      case LoanStatus.STATUS_CONTRACT_SENT:
        return 'MOBILE_VERIFICATION';
      case LoanStatus.STATUS_CONTRACT_SIGNED:
        return 'DOWNPAYMENT';
      case LoanStatusExternal.CAPTURED:
        return 'CAPTURED';
      default:
        return status;
    }
  }

  protected getNonActiveStages(): NonActiveProgressStages {
    return ORDER_PROGRESS_NON_ACTIVE_STAGES;
  }

  protected isNonActiveStatus(status: LoanStatusExternal): boolean {
    return this.NON_ACTIVE_STATUSES.includes(status);
  }

  protected isCompletedStatus(status: LoanStatusExternal): boolean {
    return this.COMPLETED_STATUSES.includes(status);
  }

  public get statuses(): OrderStatusViewStatus[] | undefined {
    return this._statuses;
  }
}

const ORDER_PROGRESS_STAGES: MilestoneProgressStages = {
  [Milestone.INITIAL]: [
    {
      nameTextId: Milestone.INITIAL,
      status: LoanStatusExternal.INITIAL,
      isMainStep: true,
      checkpointGroup: 1,
    },
    {
      nameTextId: 'PERSONAL_INFORMATION',
      status: LoanStatus.STATUS_ANALYSING,
      isMainStep: false,
      checkpointGroup: 1,
    },
  ],
  [Milestone.REQUESTED]: [
    {
      nameTextId: Milestone.REQUESTED,
      status: LoanStatusExternal.REQUESTED,
      isMainStep: true,
      checkpointGroup: 2,
    },
  ],
  [Milestone.APPROVED]: [
    {
      nameTextId: Milestone.APPROVED,
      status: LoanStatus.STATUS_LENDER_HAS_BEEN_SELECTED,
      isMainStep: true,
      checkpointGroup: 3,
    },
    {
      nameTextId: 'SMS_CODE',
      status: LoanStatus.STATUS_CONTRACT_SENT,
      isMainStep: false,
      checkpointGroup: 3,
    },
    {
      nameTextId: 'FIRST_INSTALMENT',
      status: LoanStatus.STATUS_CONTRACT_SIGNED,
      isMainStep: false,
      checkpointGroup: 3,
    },
  ],
  [Milestone.CONFIRMED]: [
    {
      nameTextId: Milestone.CONFIRMED,
      status: LoanStatusExternal.CONFIRMED,
      isMainStep: true,
      checkpointGroup: 2,
    },
    {
      nameTextId: 'CAPTURED',
      status: LoanStatusExternal.CAPTURED,
      isMainStep: false,
      checkpointGroup: 3,
    },
  ],
  [Milestone.PAID]: [
    {
      nameTextId: Milestone.PAID,
      status: LoanStatusExternal.PAID,
      isMainStep: true,
      checkpointGroup: 4,
    },
  ],
};

const ORDER_PROGRESS_NON_ACTIVE_STAGES: LoanStatusExternalProgressStages = {
  [LoanStatusExternal.DENIED]: [
    {
      nameTextId: 'DENIED',
      status: LoanStatusExternal.DENIED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatusExternal.DESISTED]: [
    {
      nameTextId: 'DESISTED',
      status: LoanStatusExternal.DESISTED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatusExternal.CANCELLED]: [
    {
      nameTextId: 'CANCELLED',
      status: LoanStatusExternal.CANCELLED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
  [LoanStatusExternal.EXPIRED]: [
    {
      nameTextId: 'EXPIRED',
      status: LoanStatusExternal.EXPIRED,
      isMainStep: true,
      checkpointGroup: 0,
    },
  ],
};
